import type { JSX } from 'react'

import { Text } from '@buffer-mono/popcorn'

import { PostPreviewsPanelView } from '~publish/components/Composer/components/PostPreviewSidePanel'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import { GUEST_NETWORKS } from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'

import {
  GuestChannelPreview,
  useGuestPreviewImages,
} from './GuestChannelPreview'
import { PublicPostPreview } from './PublicPostPreview'

export function PublicPreviewPanel({
  id,
  open,
  text,
  media,
  mediaAccess,
  networks,
  activeNetwork,
}: {
  id: string
  open: boolean
  text: string
  media: readonly GuestMedia[]
  mediaAccess: GuestMediaAccessController
  networks: readonly GuestNetworkId[]
  activeNetwork: GuestNetworkId | undefined
}): JSX.Element {
  const selectedNetworks = GUEST_NETWORKS.filter((network) =>
    networks.includes(network.id),
  )
  const { images, unavailableCount } = useGuestPreviewImages(
    selectedNetworks.length > 0 ? media : [],
    mediaAccess,
  )
  const previewNetwork = selectedNetworks.find(
    (network) => network.id === activeNetwork,
  )
  const genericPreview = (
    <PublicPostPreview text={text} media={media} mediaAccess={mediaAccess} />
  )

  return (
    <PostPreviewsPanelView
      id={id}
      open={open}
      aria-label="Post previews"
      heading={
        previewNetwork ? `${previewNetwork.name} Preview` : 'Post Previews'
      }
      isEmpty={
        selectedNetworks.length === 0 ||
        (text.trim().length === 0 && media.length === 0)
      }
    >
      {unavailableCount > 0 ? (
        <Text size="sm" color="subtle">
          {unavailableCount === 1
            ? '1 image is unavailable and isn’t shown in these previews.'
            : `${unavailableCount} images are unavailable and aren’t shown in these previews.`}
        </Text>
      ) : null}
      {previewNetwork ? (
        <GuestChannelPreview
          network={previewNetwork}
          text={text}
          images={images}
          fallback={genericPreview}
        />
      ) : null}
    </PostPreviewsPanelView>
  )
}
